// Camadas Leaflet compartilhadas pelo Mapa da Frota e pelo mapa da rota:
// marcador do veículo, trajeto e paradas detectadas.
import React from 'react';
import { CircleMarker, Marker, Polyline, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Link } from 'react-router-dom';
import type { FleetLiveVehicle, TrackPoint, TrackStop } from '../../types/vehicleTracker';
import { formatAgo } from './trackerUtils';
import { currentSpeedKmh, formatClock, formatDayClock, formatDuration, vehicleMapState, vehicleName, vehicleStateInfo } from './fleetMapUtils';

const escapeHtml = (s: string) => s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/** Ícone do veículo: caminhão na cor da situação, com a placa embaixo. */
const createVehicleIcon = (color: string, label: string, selected = false) =>
  L.divIcon({
    className: '',
    html: `<div style="display:flex;flex-direction:column;align-items:center;transform:translateY(-6px)">
      <div style="background:${color};color:white;border-radius:50%;width:${selected ? 38 : 32}px;height:${selected ? 38 : 32}px;
        display:flex;align-items:center;justify-content:center;font-size:${selected ? 19 : 16}px;
        border:${selected ? 3 : 2}px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.4)">&#128666;</div>
      <div style="margin-top:2px;background:white;color:#0f172a;font-size:10px;font-weight:600;padding:0 4px;border-radius:4px;
        box-shadow:0 1px 2px rgba(0,0,0,0.3);white-space:nowrap">${escapeHtml(label)}</div>
    </div>`,
    iconSize: [80, 52],
    iconAnchor: [40, 22],
    popupAnchor: [0, -20],
  });

interface VehicleMarkerProps {
  vehicle: FleetLiveVehicle;
  selected?: boolean;
  onSelect?: (v: FleetLiveVehicle) => void;
  /** Link para a rota em andamento (o mapa da rota não precisa dele). */
  showRouteLink?: boolean;
}

export const VehicleMarker: React.FC<VehicleMarkerProps> = ({ vehicle, selected, onSelect, showRouteLink = true }) => {
  if (vehicle.latitude == null || vehicle.longitude == null) return null;
  const info = vehicleStateInfo(vehicleMapState(vehicle));
  return (
    <Marker
      position={[vehicle.latitude, vehicle.longitude]}
      icon={createVehicleIcon(info.color, vehicle.license_plate || `#${vehicle.vehicle_id}`, selected)}
      zIndexOffset={selected ? 1000 : 500}
      eventHandlers={onSelect ? { click: () => onSelect(vehicle) } : undefined}
    >
      <Popup>
        <div className="text-sm space-y-1 min-w-[200px]">
          <strong>{vehicleName(vehicle)}</strong>
          {vehicle.is_executive && (
            <div className="text-amber-700 text-xs">Diretoria{vehicle.delivery_enabled ? ' · liberado para entregas' : ''}</div>
          )}
          <div style={{ color: info.color }} className="font-medium">
            {info.label}
            {currentSpeedKmh(vehicle) >= 1 ? ` — ${Math.round(currentSpeedKmh(vehicle))} km/h` : ''}
          </div>
          {vehicle.active_route && (
            <div>
              Rota #{vehicle.active_route.driver_route_id}
              {vehicle.active_route.driver_name ? ` — ${vehicle.active_route.driver_name}` : ''}
              <br />
              <span className="text-slate-500">
                {vehicle.active_route.total_stops - vehicle.active_route.pending_stops} de {vehicle.active_route.total_stops} paradas entregues
              </span>
              {showRouteLink && (
                <>
                  <br />
                  <Link to={`/driver-routes/${vehicle.active_route.driver_route_id}`} className="text-blue-600 hover:underline">
                    Abrir rota
                  </Link>
                </>
              )}
            </div>
          )}
          <div className="text-slate-500">
            Comunicou {formatAgo(vehicle.last_seen_seconds_ago)}
            {vehicle.last_position_at ? ` · posição de ${formatDayClock(vehicle.last_position_at)}` : ''}
          </div>
          <div className="text-slate-500">
            Bateria {vehicle.battery_percent != null ? `${vehicle.battery_percent}%` : '—'} · Sinal{' '}
            {vehicle.signal != null ? `${vehicle.signal}%` : '—'} · {vehicle.satellites ?? '—'} satélites
          </div>
        </div>
      </Popup>
    </Marker>
  );
};

/** Trajeto (posições com GPS fixado) e paradas detectadas no período. */
export const TrackLayer: React.FC<{ points: TrackPoint[]; stops: TrackStop[]; color?: string }> = ({
  points,
  stops,
  color = '#7C3AED',
}) => (
  <>
    {points.length > 1 && (
      <Polyline positions={points.map((p) => [p.latitude, p.longitude] as [number, number])} color={color} weight={4} opacity={0.75} />
    )}
    {points.length > 0 && (
      <CircleMarker center={[points[0].latitude, points[0].longitude]} radius={6} pathOptions={{ color: 'white', weight: 2, fillColor: color, fillOpacity: 1 }}>
        <Popup>Início do trajeto — {formatClock(points[0].at)}</Popup>
      </CircleMarker>
    )}
    {stops.map((s) => (
      <CircleMarker
        key={`${s.start_at}-${s.latitude}`}
        center={[s.latitude, s.longitude]}
        radius={8}
        pathOptions={{ color: 'white', weight: 2, fillColor: s.ongoing ? '#DC2626' : '#F59E0B', fillOpacity: 0.95 }}
      >
        <Popup>
          <div className="text-sm">
            <strong>{s.ongoing ? 'Parado agora' : 'Parada'}</strong>
            <br />
            {formatDayClock(s.start_at)} {s.end_at ? `até ${formatClock(s.end_at)}` : ''}
            <br />
            Duração: {formatDuration(s.duration_seconds)}
          </div>
        </Popup>
      </CircleMarker>
    ))}
  </>
);
